import {
  COMPANY_TYPE_LABEL,
  DIRECTOR_ROLE_LABEL,
  DOCUMENT_LABEL,
  DOCUMENT_KINDS,
  type KybDraft,
} from '~/lib/kyb';
import { SectionCard } from './Primitives';

/**
 * Last stop before submit. Reads flat from the draft, mirrors the order the
 * user filled things in. Clicking a step pill at the top of the stepper
 * (handled by the parent) is how edits happen — this screen does not try to
 * be an editor.
 */
export function ReviewStep({ draft }: { draft: KybDraft }): JSX.Element {
  return (
    <SectionCard
      title="Review and submit"
      description="Once submitted, sections lock while a reviewer looks at your file. We'll email the owner on file with the decision."
    >
      <div className="flex flex-col gap-5">
        <ReadBlock title="Business">
          <Pair label="Registered name" value={draft.appliedCompanyName} />
          <Pair
            label="Type"
            value={
              draft.companyType ? COMPANY_TYPE_LABEL[draft.companyType] : '—'
            }
          />
          <Pair label="RC number" value={draft.rcNumber} mono />
          <Pair label="TIN" value={draft.tin} mono />
          <Pair label="What it does" value={draft.businessDescription} wide />
        </ReadBlock>

        <ReadBlock title="Operating address">
          <Pair
            label="Address"
            value={`${draft.addressLine}, ${draft.city}, ${draft.state}`}
            wide
          />
          <Pair label="Contact phone" value={draft.contactPhone} />
          <Pair label="Contact email" value={draft.contactEmail} />
        </ReadBlock>

        <ReadBlock title={`Leadership (${draft.directors.length})`}>
          {draft.directors.map((d, i) => (
            <div
              key={i}
              className="col-span-2 rounded border border-rule-hairline bg-surface-canvas p-3"
            >
              <div className="flex items-baseline justify-between">
                <div className="text-[14px] font-semibold text-ink-primary">
                  {d.firstName} {d.lastName}
                </div>
                <div className="text-[11.5px] text-ink-watermark">
                  {DIRECTOR_ROLE_LABEL[d.role]} · {d.nationality}
                </div>
              </div>
              <div className="mt-1 flex items-center gap-4 text-[13px] text-ink-secondary">
                <span>{d.email}</span>
                <span className="font-mono text-ink-watermark">
                  BVN ···{d.bvn.slice(-4)}
                </span>
              </div>
            </div>
          ))}
        </ReadBlock>

        <ReadBlock title="Documents">
          {DOCUMENT_KINDS.map((kind) => {
            const state = draft.documents[kind];
            const value =
              state.kind === 'uploaded' ? state.fileName : 'Not uploaded';
            return <Pair key={kind} label={DOCUMENT_LABEL[kind]} value={value} />;
          })}
        </ReadBlock>
      </div>
    </SectionCard>
  );
}

function ReadBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div className="mb-2 text-[11.5px] font-medium uppercase tracking-[0.08em] text-ink-watermark">
        {title}
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Pair({
  label,
  value,
  mono,
  wide,
}: {
  label: string;
  value: string;
  mono?: boolean;
  wide?: boolean;
}): JSX.Element {
  return (
    <div className={wide ? 'col-span-full' : undefined}>
      <div className="text-[11.5px] text-ink-watermark">{label}</div>
      <div
        className={`mt-0.5 whitespace-pre-wrap text-[14px] text-ink-primary ${
          mono ? 'font-mono' : ''
        }`}
      >
        {value || '—'}
      </div>
    </div>
  );
}
